import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { type StaticDecode, Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import { formatUnit } from 'auto-converted/lib/UnitHelper';
import {
  LocString,
  Nullable,
  ReadonlyArray,
  ReadonlyInterface,
  ReadonlyObject,
} from 'schema/schema';
import { Image, MongoAndPgEntity } from 'schema/shared';
import { useLogUpdatedCVs } from 'stores/ActivityLogs';
import { httpChecked, queryClient } from 'util/fetch';
import { I18n } from 'util/translations';

/**
 * The version of User returned from the API calls to examine usage of project fields on CVs
 */
export const UsageUser = ReadonlyInterface([MongoAndPgEntity], {
  name: Type.String(),
  cv_id: Type.String(),
  can_write: Type.Boolean(),
  image: Image,
  project_experience_ids: ReadonlyArray(Type.String()),
});
export type UsageUser = StaticDecode<typeof UsageUser>;
const UsageUsers = ReadonlyArray(UsageUser);

const UsersByVariant = Type.Record(Type.String(), UsageUsers);

export const FieldUsage = ReadonlyObject({
  official_value: Nullable(Type.String()),
  users_with_official_value: UsageUsers,
  users_with_variants: UsageUsers,
  users_with_variants_ro: UsageUsers,
  variants: ReadonlyArray(Nullable(Type.String())),
  users_by_variant: UsersByVariant,
  users_by_variant_ro: UsersByVariant,
});
export type FieldUsage = StaticDecode<typeof FieldUsage>;

export const CustomerContactUsage = ReadonlyObject({
  name: Type.String(),
  label: LocString,
  local_language_label: Type.String(),
  official: Type.Boolean(),
  users: UsageUsers,
});
export type CustomerContactUsage = StaticDecode<typeof CustomerContactUsage>;

const OverviewFields = ReadonlyObject({
  customer_contacts: ReadonlyArray(CustomerContactUsage),
  invisible_cvs: Type.Integer({ minimum: 0 }),
});

const FieldUsages = ReadonlyObject({
  // values
  dates: FieldUsage,
  project_extent_hours: FieldUsage,
  total_extent_hours: FieldUsage,
  project_extent: FieldUsage,
  total_extent: FieldUsage,
  area: FieldUsage,
  location_country_code: FieldUsage,
  project_address: FieldUsage,

  // locstrings
  industry: FieldUsage, // should only be customer, not project
  type: FieldUsage,
  contract_type: FieldUsage,
});
type FieldUsages = StaticDecode<typeof FieldUsages>;

export type FieldName = keyof FieldUsages;

const fieldsThatAreLocStrings: ReadonlySet<FieldName> = new Set([
  'industry',
  'type',
  'contract_type',
]);

export function mapProjectField(
  field: FieldName,
  value: string | null | undefined,
) {
  if (field === 'location_country_code') {
    const formattedValue =
      value == null
        ? value
        : I18n.t(value.toUpperCase(), { scope: 'countries' });
    return { type: 'values', value: formattedValue } as const;
  }

  if (field === 'area') {
    const formattedValue = value == null ? value : formatUnit(value);
    return { type: 'values', value: formattedValue } as const;
  }

  if (fieldsThatAreLocStrings.has(field)) {
    return { type: 'locstring', value } as const;
  }

  return { type: 'values', value } as const;
}

const OverviewSchema = Type.Intersect([OverviewFields, FieldUsages]);

const checker = TypeCompiler.Compile(OverviewSchema);

const queries = {
  overview: ['project_field_overview'] as const,
  project: (customer_id: string, project_id: string) =>
    [...queries.overview, { customer_id, project_id }] as const,
  fetch: (customer_id: string, project_id: string, language_code: string) =>
    queryOptions({
      queryKey: [
        ...queries.project(customer_id, project_id),
        { language_code },
      ] as const,
      queryFn: ({ signal }) => {
        const url = new URL(
          '/api/v2/company/cv/customers/project_experiences/overview',
          window.location.origin,
        );

        url.searchParams.set('language_code', language_code);
        url.searchParams.set('customer_id', customer_id);
        url.searchParams.set('project_id', project_id);

        return httpChecked.get(url, checker, { signal });
      },
    }),
};

export function useProjectFieldOverview(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  const { data } = useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    staleTime: 10_000,
  });

  return data;
}

export function useProjectFieldCustomerContacts(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  const { data } = useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    select: (data) => data.customer_contacts,
    staleTime: 10_000,
  });

  return data;
}

const synchroniseFieldSchema = ReadonlyObject({
  changed: Type.Integer(),
  unchanged: Type.Integer(),
  cannot_write: Type.Integer(),
  updated_cvs: ReadonlyArray(
    ReadonlyObject({
      user_id: Type.String(),
      cv_id: Type.String(),
      name: Nullable(Type.String()),
    }),
  ),
});

const synchroniseFieldChecker = TypeCompiler.Compile(synchroniseFieldSchema);

interface SynchroniseFieldVariables {
  field_group: FieldName | 'all';
  project_experience_ids?: readonly string[];
}

export function useSynchroniseProjectFieldOverview(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  const logUpdatedCVs = useLogUpdatedCVs();

  return useMutation({
    mutationFn: ({
      field_group,
      project_experience_ids,
    }: SynchroniseFieldVariables) => {
      const body = {
        customer_id,
        project_id,
        country_code: language_code,
        field_group,
        project_experience_ids,
      };

      return httpChecked.put(
        '/api/v2/company/cv/customers/project_experiences/synchronise',
        synchroniseFieldChecker,
        { body },
      );
    },
    onSuccess: (data) => {
      logUpdatedCVs(data.updated_cvs);

      return invalidateProjectFieldsOverview(customer_id, project_id);
    },
  });
}

export async function invalidateProjectFieldsOverview(
  customer_id: string,
  project_id: string,
) {
  await queryClient.invalidateQueries({
    queryKey: queries.project(customer_id, project_id),
  });
}

export async function invalidateAllProjectFieldsOverviews() {
  await queryClient.invalidateQueries({ queryKey: queries.overview });
}

export async function prefetchProjectFieldOverview(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  await queryClient.prefetchQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    staleTime: Infinity,
  });
}
